import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { ApiError, type FilterClause, type WhereGroup } from '../api/client';
import type { ColumnState, SortState } from '../views/DataGrid';
import type { ViewKind } from '../meta/types';
import { useToast } from '../design/primitives/Toast';

// Saved views: /v1/saved-views (blueprint §5.3 saved_view: resource, name, shared, query,
// columns, view_type). Absent backend → an empty list (never an error banner).

export interface SavedView {
  id: string;
  resource: string;
  name: string;
  shared: boolean;
  query: { filters?: FilterClause[]; where?: WhereGroup | null; sort?: SortState[]; q?: string; groupBy?: string | null };
  columns?: ColumnState | null;
  view_type: ViewKind;
  owner_user_id?: string | null;
}

export function useSavedViews(resource: string) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['saved-views', resource];
  const list = useQuery<SavedView[]>({
    queryKey: key,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        const page = await api.resource<SavedView>('saved-views').list({ filter: [{ field: 'resource', op: 'eq', value: resource }], limit: 100 });
        return page.data;
      } catch (e) {
        if (e instanceof ApiError && (e.isNotFound || e.isNetwork || e.status === 501)) return [];
        throw e;
      }
    },
  });
  const save = useMutation({
    mutationFn: (v: Omit<SavedView, 'id'> & { id?: string }) => (v.id ? api.resource<SavedView>('saved-views').update(v.id, v) : api.resource<SavedView>('saved-views').create(v)),
    onSuccess: (v) => {
      toast({ title: `View “${v.name}” saved` });
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast({ kind: 'error', title: 'Could not save view', detail: e instanceof ApiError ? e.detail : String(e) }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.resource('saved-views').remove(id),
    onSuccess: () => {
      toast({ title: 'View deleted' });
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast({ kind: 'error', title: 'Could not delete view', detail: e instanceof ApiError ? e.detail : String(e) }),
  });
  return { views: list.data ?? [], loading: list.isLoading, save, remove };
}
